/**
 *    ANGULAR DELEGATE
 *    gets all data from Angular $rootScope and session
 * 	  sends events for error / http / msg to Angular App
 */

// platform
import angular from 'angular';
import cloneDeep from 'lodash/cloneDeep';

// app
import validSvc from '../validSvc';
import utilSvc from '../utilSvc';
import sessionRepo from '../repo/sessionRepo';

// types
import { AppConfig, ErrorVM, INgRootDelegate, Insights, NgContext, UserInfo } from '../../../global';

// Angular Context is a shared singleton
let _ngContext: NgContext = ngContextVM();

type NgContextState = {
    appConfig: AppConfig;
    sessionToken: string;
    isDev: boolean;
    isDebug: boolean;
    apiLog: boolean;
    User: UserInfo | null;
    isVerity: boolean;
    insights: Insights;
    biVersion: string;
};

function ngContextVM(): NgContextState {
    return {
        appConfig: {} as AppConfig,
        sessionToken: '',
        isDev: false,
        isDebug: false,
        apiLog: false,
        User: null,
        isVerity: false,
        insights: {} as Insights,
        biVersion: '',
    };
}

// CONTEXT ACCESSOR
export const ngContext = (): NgContext => {
    return cloneDeep(_ngContext); // de-ref to keep original as-is
};

// ANGULAR ACCESSOR
export const ngDelegate = {
    load: load,
    unload: unload,
    errorCatch: errorCatch,
    error: errorMsg,
    warn: warnMsg,
    ok: okMsg,
    wait: waitMsg,
    pongTimer: emitPongTimer,
    reloadStartUp: emitStartUp,
    logout: emitLogout,
    rootScopeUpdate: rootScopeUpdate,
    helpLink,
};

/**
 *    STARTUP
 * 	  connect to Angular
 */

function getNgRootScope(): INgRootDelegate {
    // use root element and injector to get a handle on Angular $rootScope memory object

    const ngRootElem = document.getElementById('root');
    if (!!!ngRootElem) {
        throw new Error('ngDelegate, cannot get angular root element');
    }

    const injector = angular.element(ngRootElem).injector();
    if (!!!injector) {
        throw new Error('ngDelegate, cannot get angular injector');
    }

    const ngRootScope = injector.get('$rootScope');
    if (!!!ngRootScope) {
        throw new Error('ngDelegate, cannot get angular root from injector');
    }

    return ngRootScope as INgRootDelegate;
}

function load(): void {
    // called by React error boundary to load up React state on first component use
    console.log('ng delegate load');

    // reset, re-get Angular rootScope
    _ngContext = ngContextVM();
    const ngRoot = getNgRootScope();

    // check user
    _ngContext.User = ngRoot.User || null;
    if (!!ngRoot.User) {
        _ngContext.isVerity = ngRoot.User.Type == 'VERITY' || ngRoot.User.Type == 'VERITYSA';
    }

    // transfer config to v2 context
    var appConfig = ngRoot.appConfig || {};
    _ngContext.isDev = appConfig.env == 'local';
    _ngContext.appConfig.apiPath = validSvc.isText(appConfig.apiPath) ? appConfig.apiPath : '/api/'; // default api path
    _ngContext.insights = ngRoot.appConfig.insights || ({} as Insights);

    // set context flags
    _ngContext.isDebug = !!ngRoot.debug;
    _ngContext.apiLog = !!ngRoot.apiLog;
    _ngContext.biVersion = ngRoot.BIVersion;

    // get session
    /* @ts-expect-error do-not-change */
    const sess: SessionStoreObject | null = sessionRepo.get('espui') as SessionStoreObject;
    if (!!sess && !!sess.token && validSvc.isText(sess.token)) {
        _ngContext.sessionToken = sess.token;
    }
}

function unload(): void {
    // called by React error boundary to unload React state
    console.log('ng delegate unload');
    _ngContext = ngContextVM();
}

/**
 * 	NG ROOTSCOPE EVENT
 * 	graps Angular rootscope then fires event inside Angular
 * 	all react events start with 'react>' and are caught in App.js
 */

function emitRoot(eventName: string, data?: any): void {
    // params
    if (!validSvc.isText(eventName)) {
        errorMsg('ng.delegate.emitRoot, eventName required');
        return;
    }

    const ngRoot = getNgRootScope();
    ngRoot.$emit(eventName, data || {});
}

/**
 * 	HTTP LOGOUT
 * 	on 401 or 423 http service will call logout
 * 	this will send user back to login page
 */

function emitLogout(reason?: string): void {
    emitRoot('espui>auth.logout', { reason: reason || '' });
}

/**
 * 	PONG TIMER
 * 	reset activity timer Angular uses to track user inactivity
 */

function emitPongTimer(action: string): void {
    emitRoot('react>pong-timer', { action: action });
}

/**
 * 	RE-RUN APP START UP
 * 	Verity login found Verity session token, re-run start up to validate app status
 */

function emitStartUp(): void {
    console.log('VLOG: ngDelegate emit startup');
    emitRoot('espui>startup', {});
}

/**
 * 	ERROR / WARN / OK MESSAGING BACK TO ANGULAR
 * 	note: look at Angular listeners in App.ts 'react>...'
 */

function errObj(msgType: string, msg: string, stack: string, status: string, detail: string): ErrorVM {
    // convenience, turn params into an ErrorVM object
    return {
        msgType: msgType,
        msg: msg,
        stack: stack,
        status: status,
        detail: detail,
    };
}

function errorMsg(msg: string, stack?: string, status?: string, detail?: string): void {
    // fires main Angular error display (will reset the app, use wisely)
    emitRoot('react>error-warning', errObj('error.unexpected', msg, stack || '', status || '', detail || ''));
}

function errorCatch(err?: any, stack?: string): void {
    // will ignore or throw unexpected errors
    if (utilSvc.http.isIgnore(err)) {
        return;
    }
    err = (err as object) || {};
    const errMsg = typeof err.message === 'string' ? err.message : 'unknown';
    stack = !!stack ? stack : 'unknown';
    let detail = '';
    try {
        detail = JSON.stringify(err);
    } catch (err) {
        detail = '';
    } finally {
        emitRoot('react>error-warning', errObj('error.unexpected', errMsg, stack, '', detail));
    }
}

function warnMsg(msg: string, stack?: string, status?: string, detail?: string): void {
    // fires main Angular error display (will reset the app, use wisely)
    emitRoot('react>error-warning', errObj('error.warn', msg, stack || '', status || '', detail || ''));
}

function helpLink(folder, title): void {
    if (typeof folder !== 'string' || folder.trim() === '') {
        alert('help folder missing');
        return;
    }

    if (typeof title !== 'string') {
        title = '';
    }

    // emit event
    emitRoot('espui>msgbox.show', {
        msgType: 'help',
        title: title,
        msg: '',
        msgId: '',
        folder: folder,
    });
    emitRoot('react>refresh');
}

function okMsg(title: string, msg?: string): void {
    // fires main Angular msg display for OK style messages, no user response supported yet
    emitRoot('espui>msgbox.show', {
        msgType: 'ok',
        title: title,
        msg: msg || '',
    });
    emitRoot('react>refresh');
}

function waitMsg(isWaiting, immediate = false): void {
    // show the waiting spinner
    // emit event
    emitRoot('espui>msgbox.show', {
        msgType: 'wait',
        wait: !!isWaiting ? 'on' : 'off',
        immediate: !!immediate,
    });
    emitRoot('react>refresh');
}

function rootScopeUpdate(prop: string, val: any): void {
    // update Angular rootScope from React
    if (validSvc.isText(prop)) {
        emitRoot('react>rootscope-update', { prop: prop, val: val });
        console.log('NG DEL: update prop: ' + prop);
    }
}
